import get from "lodash/get";

/**
 * Maps story card data to section props
 * Designed for performance with minimal iterations
 */

/**
 * Parse story elements to extract text content for rich text (intro paragraph)
 * Looks for first text element or summary element
 */
const parseStoryElementsToRichText = (storyElements) => {
  if (!Array.isArray(storyElements) || storyElements.length === 0) {
    return null;
  }

  // Find the first text element (usually the intro/heading)
  const textElement = storyElements.find(el => el.type === 'text' && !el.subtype);
  if (textElement && textElement.text) {
    return textElement.text;
  }

  return null;
};

/**
 * Extract highlight text (summary) from story elements
 */
const extractHighlightText = (storyElements) => {
  if (!Array.isArray(storyElements) || storyElements.length === 0) {
    return null;
  }

  // Find the summary element
  const summaryElement = storyElements.find(el => el.type === 'text' && el.subtype === 'summary');
  if (summaryElement && summaryElement.text) {
    return summaryElement.text.replace(/<[^>]*>/g, '').trim();
  }

  return null;
};

/**
 * Extract highlight heading from story elements
 * Preserves HTML tags for proper rendering
 */
const extractHighlightHeading = (storyElements) => {
  if (!Array.isArray(storyElements) || storyElements.length === 0) {
    return null;
  }
  // Find the heading element and preserve HTML tags
  const headingElement = storyElements?.[0]?.text;
  return headingElement;
};

/**
 * Parse story elements to extract card data (Q&A format)
 */
const parseStoryElementsToCards = (storyElements) => {
  if (!Array.isArray(storyElements) || storyElements.length === 0) {
    return [];
  }

  const cards = [];
  // Filter for q-and-a elements
  const qaElements = storyElements.filter(el => el.type === 'text' && el.subtype === 'q-and-a');

  for (const element of qaElements) {
    // Check if element has metadata with Q and A
    const metadata = element.metadata || {};
    const q = metadata.question || '';
    const a = metadata.answer || '';

    if (q && a) {
      cards.push({
        title: q.replace(/<[^>]*>/g, '').trim(),
        content: a.replace(/<[^>]*>/g, '').trim()
      });
    }
  }

  return cards;
};

/**
 * Parse story elements for special cards (ConferencesSection)
 * Structure: text element (no subtype) = card title, followed by q-and-a elements = sections
 */
const parseStoryElementsToSpecialCards = (storyElements) => {
  if (!Array.isArray(storyElements) || storyElements.length === 0) {
    return [];
  }

  const specialCards = [];
  let currentCard = null;

  for (const element of storyElements) {
    // If it's a text element with no subtype, it's a special card title
    if (element.type === 'text' && !element.subtype && element.text) {
      // Save previous card if exists
      if (currentCard && currentCard.sections.length > 0) {
        specialCards.push(currentCard);
      }

      // Start new card, strip markdown ** and HTML tags from title
      const title = element.text.replace(/\*\*/g, '').replace(/<[^>]*>/g, '').trim();
      currentCard = {
        title: title,
        sections: []
      };
    }
    // If it's a q-and-a element and we have a current card, add it as a section
    else if (element.type === 'text' && element.subtype === 'q-and-a' && currentCard) {
      const metadata = element.metadata || {};
      const q = metadata.question || '';
      const a = metadata.answer || '';

      if (q && a) {
        currentCard.sections.push({
          title: q.replace(/<[^>]*>/g, '').trim(),
          content: a.replace(/<[^>]*>/g, '').trim()
        });
      }
    }
  }

  // Don't forget to add the last card
  if (currentCard && currentCard.sections.length > 0) {
    specialCards.push(currentCard);
  }

  return specialCards;
};

/**
 * Extract tags from eventcategories attribute
 */
const extractTags = (attributes) => {
  const eventCategories = get(attributes, ['eventcategories'], []);
  return Array.isArray(eventCategories) && eventCategories.length > 0
    ? eventCategories.map(tag => tag.toLowerCase())
    : [];
};

/**
 * Parse story elements for BecomeAMemberSection component
 * Structure: [title, description, button]
 */
const parseStoryElementsToBecomeAMember = (storyElements) => {
  if (!Array.isArray(storyElements) || storyElements.length === 0) {
    return null;
  }

  const memberSectionData = {
    title: null,
    description: null,
    button: null
  };
  // Extract data from the 3 story elements
  storyElements.forEach((element, index) => {
    if (!element || !element.text) return;

    const cleanText = element.text.replace(/<[^>]*>/g, '').trim();

    switch (index) {
      case 0:
        // First element: title
        memberSectionData.title = cleanText;
        break;
      case 1:
        // Second element: description
        memberSectionData.description = cleanText;
        break;
      case 2:
        // Third element: button
        memberSectionData.button = {
          text: cleanText,
          url: element?.metadata['cta-url'],
        };
        break;
      default:
        break;
    }
  });
  return memberSectionData;
};

/**
 * Fetches events collection data from API
 * Shared function for all event-related pages
 */
export const fetchEventsCollection = async (eventType) => {
  try {
    const response = await fetch(`/api/v1/collections/${eventType}`);
    if (!response.ok) throw new Error("Failed to fetch events collection");
    const collectionData = await response.json();

    // Get 0th index story from collection items
    const items = collectionData.items || [];
    const eventStories = items?.filter(item => ['conference', 'workshop', 'session'].includes(item?.story?.["story-template"]));
    const heroStory = eventStories[0] || null;

    return { heroStory, collectionData };
  } catch (error) {
    console.error("Error fetching events collection:", error);
    return { heroStory: null, collectionData: null, error };
  }
};

/**
 * Maps collection story data to hero banner structure
 * Can be used across different event pages (events, conferences, workshops)
 */
export const mapCollectionStoryToHeroData = (heroStory, collectionData) => {
  const { story } = heroStory;
  const {
    alternative,
    headline,
    subheadline,
    metadata = {},
    sections = [],
    url
  } = story;
  // Enhanced data extraction based on the latest implementation
  const eventDate = metadata?.duration || "";
  const eventLocation = metadata?.where || "";
  const eventForum = metadata?.["session-forum"] || "";
  const eventWhen = metadata?.when || "";
  const storyTemplate = story?.['story-template'] || "";

  const breadcrumbsMap = {
    events: [{ label: "Events", href: "/events" }],
    conferences: [
      { label: "Events", href: "/events" },
      { label: "Conferences"}
    ],
    workshops: [
      { label: "Events", href: "/events" },
      { label: "Workshops" }
    ],
    sessions: [
      { label: "Events", href: "/events" },
      { label: "Sessions" }
    ]
  };
  const eventInfoDetails = storyTemplate === "session" ? [eventLocation] : [eventDate, eventLocation];

  return {
    backgroundImage: collectionData?.metadata?.["cover-image"]?.url || null,
    breadcrumbs: breadcrumbsMap[sections?.[0]?.slug],
    title: alternative?.home?.default?.headline || headline,
    storyUrl: url,
    eventInfo: {
      title: alternative?.home?.default?.subheadline || subheadline || "",
      details: eventInfoDetails.filter(Boolean),
      eventForum,
      eventWhen,
      storyTemplate
    },
    buttons: [
      {
        label: "Get updates",
        variant: "outlined",
        href: `${url}?openModal=keepMeInformed`
      },
      // Only include "Book a seat" button if storyTemplate is not 'session'
      ...(storyTemplate !== "session" ? [{
        label: "Book a seat",
        variant: "primary",
        href: `${url}#book-seat`
      }] : [])
    ],
  };
};

/**
 * Main mapper function - processes story and returns all section data
 * Single pass through the story cards for optimal performance
 */
export const mapStoryToSections = (story) => {
  if (!story || !story.cards || !Array.isArray(story.cards)) {
    return null;
  }

  const cards = story.cards;
  const sections = {
    eventsContent: null,
    conferences: null,
    workshops: null,
    sessions: null,
    becomeAMember: null
  };

  // Map each card to its corresponding section
  // Card 0: Events intro content
  // Card 1: Conferences basic info
  // Card 2: Conferences special cards (Forum/Thematic)
  // Card 3: Workshops
  // Card 4: Sessions
  // Card 5: BecomeAMemberSection data

  cards.forEach((card, index) => {
    const storyElements = get(card, ['story-elements'], []);
    const attributes = get(card, ['metadata', 'attributes'], {});
    const tags = extractTags(attributes);

    switch (index) {
      case 0:
        // Events Content Section
        sections.eventsContent = {
          content: parseStoryElementsToRichText(storyElements)
        };
        break;

      case 1:
        // Conferences Section - Basic Cards
        sections.conferences = {
          tags,
          highlightText: extractHighlightText(storyElements),
          basicCards: parseStoryElementsToCards(storyElements),
          heading: extractHighlightHeading(storyElements),
          specialCards: [] // Will be populated by card 2
        };
        break;

      case 2:
        // Conferences Section - Special Cards
        if (sections.conferences) {
          sections.conferences.specialCards = parseStoryElementsToSpecialCards(storyElements);
        }
        break;

      case 3:
        // Workshops Section
        sections.workshops = {
          tags,
          highlightText: extractHighlightText(storyElements),
          cards: parseStoryElementsToCards(storyElements),
          heading: extractHighlightHeading(storyElements),
        };
        break;

      case 4:
        // Sessions Section
        sections.sessions = {
          tags,
          highlightText: extractHighlightText(storyElements),
          cards: parseStoryElementsToCards(storyElements),
          heading: extractHighlightHeading(storyElements),
        };
        break;

      case 5:
        // BecomeAMemberSection
        sections.becomeAMember = parseStoryElementsToBecomeAMember(storyElements);
        break;

      default:
        break;
    }
  });

  return sections;
};
